import { api } from './client';
import type {
  ApiResponse,
  AuthTokens,
  ServiceProviderProfile,
  CertificationTier,
  ClientProject,
  Project,
  ServiceEngagement,
  User,
} from '@/types';

export interface ClientProjectCreationResponse {
  clientProject: ClientProject;
  project: Project;
  engagement: ServiceEngagement;
  organization: {
    id?: string;
    _id?: string;
    name?: string;
    slug?: string;
    [key: string]: unknown;
  };
}

export interface HandoffTokenValidationResponse {
  propertyName: string;
  address: ClientProject['address'];
  serviceProviderName: string;
  serviceProviderBusinessName?: string;
}

export interface HandoffAcceptanceResponse {
  user: User;
  tokens: AuthTokens;
  project: Project;
}

export interface ServiceProviderDashboardData {
  profile: ServiceProviderProfile | null;
  activeEngagementCount: number;
  completedEngagements: number;
  certificationTier: CertificationTier;
  ratings: { average: number; count: number };
  engagements: unknown[];
  clientProjects: unknown[];
}

export const serviceProviderApi = {
  createProfile: (data: Partial<ServiceProviderProfile>) =>
    api.post<{ profile: ServiceProviderProfile }>('/service-provider/profile', data),

  getMyProfile: () =>
    api.get<ServiceProviderProfile>('/service-provider/profile/me'),

  updateMyProfile: (data: Partial<ServiceProviderProfile>) =>
    api.put<ServiceProviderProfile>('/service-provider/profile/me', data),

  getDashboard: () =>
    api.get<ServiceProviderDashboardData>('/service-provider/dashboard'),

  // Client project endpoints
  createClientProject: (data: {
    name: string;
    customerEmail: string;
    projectType: string;
    customerName?: string;
    address?: { street?: string; city?: string; state?: string; postalCode?: string; country?: string };
    specializationScope?: string;
    sourceTemplateId?: string;
  }) =>
    api.post<ClientProjectCreationResponse>('/client-projects', data),

  listClientProjects: (params?: { limit?: number; offset?: number; status?: string }) =>
    api.get<{ clientProjects: ClientProject[]; total: number }>('/client-projects', { params }),

  getClientProject: (id: string) =>
    api.get<ClientProject>(`/client-projects/${id}`),

  updateClientProject: (id: string, data: Partial<ClientProject>) =>
    api.put<ClientProject>(`/client-projects/${id}`, data),

  setProvisioningConfig: (id: string, config: {
    homeProjectId?: string;
    homePageId?: string;
    quickPages?: Array<{ pageId: string; label: string; icon?: string }>;
    theme?: string;
  }) =>
    api.put<ClientProject>(`/client-projects/${id}/provisioning`, config),

  // Engagement endpoints
  createEngagement: (clientProjectId: string, data: {
    specializationScope: string;
    accessWindow: { startDate: string; endDate: string };
    serviceProviderEmail?: string;
    serviceProviderId?: string;
  }) =>
    api.post<ServiceEngagement>(`/client-projects/${clientProjectId}/engagements`, data),

  listEngagements: (clientProjectId: string) =>
    api.get<ServiceEngagement[]>(`/client-projects/${clientProjectId}/engagements`),

  approveEngagement: (id: string) =>
    api.post<ServiceEngagement>(`/engagements/${id}/approve`),

  acceptEngagement: (id: string) =>
    api.post<ServiceEngagement>(`/engagements/${id}/accept`),

  completeEngagement: (id: string) =>
    api.post<ServiceEngagement>(`/engagements/${id}/complete`),

  extendEngagement: (id: string, data: { newEndDate: string }) =>
    api.post<ServiceEngagement>(`/engagements/${id}/extend`, data),

  revokeEngagement: (id: string) =>
    api.delete<ServiceEngagement>(`/engagements/${id}`),

  // Handoff endpoints (Phase 3)
  initiateHandoff: (clientProjectId: string) =>
    api.post<{ success: boolean; emailSent: boolean; tokenExpiry: string }>(`/client-projects/${clientProjectId}/handoff`),
  validateHandoffToken: (token: string) =>
    api.get<HandoffTokenValidationResponse>(`/client-projects/invitation/${token}`),
  acceptHandoff: (token: string) =>
    api.post<HandoffAcceptanceResponse>(`/client-projects/invitation/${token}/accept`),
  acceptHandoffWithRegistration: (token: string, data: { email: string; password: string; firstName: string; lastName: string }) =>
    api.post<HandoffAcceptanceResponse>(`/client-projects/invitation/${token}/accept-register`, data),
  declineHandoff: (token: string) =>
    api.post<{ success: boolean }>(`/client-projects/invitation/${token}/decline`),

  // Multi-project per client project (Phase 4)
  addLinkedProject: (clientProjectId: string, data: { projectId: string; specializationScope?: string }) =>
    api.post(`/client-projects/${clientProjectId}/projects`, data),
  removeLinkedProject: (clientProjectId: string, projectId: string) =>
    api.delete(`/client-projects/${clientProjectId}/projects/${projectId}`),
};
